import { useEffect, useState } from "react";
import { Check, Copy, ExternalLink, Loader2 } from "lucide-react";
import api, { PLATFORM_NAME, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LockedFeature } from "@/components/LockedFeature";
import { FlatRates } from "@/components/FlatRates";
import { MapsStatusBanner } from "@/components/MapsStatusBanner";
import { LoyaltyPanel } from "@/components/LoyaltyPanel";
import { PaymentPolicyPanel } from "@/components/PaymentPolicyPanel";
import { PayoutPolicyCard } from "@/components/PayoutPolicyCard";
import { StoreIconUpload } from "@/components/StoreIconUpload";
import { AppDesigner } from "@/components/AppDesigner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const CopyLink = ({ url, testId }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div className="mt-3 flex items-center gap-2">
      <code data-testid={`${testId}-url`} className="min-w-0 flex-1 truncate rounded border border-ink-600 bg-ink-900 px-3 py-2 font-mono text-[11px] text-slate-300">
        {url}
      </code>
      <Button data-testid={`${testId}-copy`} variant="outline" size="sm" aria-label="Copy link"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(url);
          } catch {
            const t = document.createElement("textarea");
            t.value = url;
            document.body.appendChild(t);
            t.select();
            document.execCommand("copy");
            t.remove();
          }
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }}
        className="shrink-0 border-ink-600 bg-transparent text-slate-300 hover:text-gold">
        {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
      </Button>
      <Button asChild variant="outline" size="sm" className="shrink-0 border-ink-600 bg-transparent text-slate-300 hover:text-gold">
        <a href={url} target="_blank" rel="noreferrer" aria-label="Open link"><ExternalLink className="h-4 w-4" /></a>
      </Button>
    </div>
  );
};

const Field = ({ label, testId, children, ...rest }) => (
  <div>
    <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-gold">{label}</label>
    {children || (
      <Input data-testid={testId} className="h-11 border-ink-600 bg-ink-800 text-slate-100 focus-visible:ring-gold" {...rest} />
    )}
  </div>
);

const Panel = ({ title, description, children }) => (
  <div className="rounded-xl border border-ink-600 bg-ink-800/60 p-6 md:p-8">
    <h2 className="text-lg font-medium text-slate-100">{title}</h2>
    {description && <p className="mt-2 text-sm text-slate-400">{description}</p>}
    <div className="mt-6 space-y-5">{children}</div>
  </div>
);

const DOMAIN_STATES = {
  requested: { label: "requested", hint: "We have your request — support sets up DNS and SSL, usually within a business day." },
  in_progress: { label: "being set up", hint: "Support is wiring this up now. Add the DNS record below if you have not already." },
  active: { label: "live", hint: "Your domain is live and serving your booking page over HTTPS." },
  declined: { label: "not set up", hint: "Support could not complete this one — see the note below." },
};

export default function Settings() {
  const { company, setCompany, hasFlag } = useAuth();
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get("tab") || "company");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [dns, setDns] = useState([]);

  const [profile, setProfile] = useState({ name: "", address: "", phone: "", email: "", logo_url: "", tax_id: "" });
  const [contact, setContact] = useState({ contact_phone: "", contact_email: "", contact_hours: "", contact_note: "" });
  const [branding, setBranding] = useState({
    primary_color: "#D4AF37", accent_color: "#0F131C", font: "Outfit",
    email_header: "", email_footer: "", manifest_note: "",
  });
  const [domain, setDomain] = useState("");
  const [handle, setHandle] = useState("");
  const [vanity, setVanity] = useState("");
  const [sms, setSms] = useState({ configured: false, from_number: "", plan_allows: true });
  const [emailCfg, setEmailCfg] = useState({ email_mode: "standard", reply_to_email: "", sender_name: "", sender_email: "" });
  const [keys, setKeys] = useState({ stripe_publishable_key: "", stripe_secret_key: "" });
  const [rules, setRules] = useState({ grace_minutes: 15, airport_grace_minutes: 45, stop_grace_minutes: 10, auto_apply: true, late_pickup_alerts: true, late_pickup_lead_minutes: 20, proximity_alert_minutes: 5, dispatch_alert_number: "", dispatch_alert_email: "", passenger_changes_enabled: true, change_cutoff_hours: 24, change_fee: 0, change_fee_within_hours: 4, timezone: "UTC" });
  const [digest, setDigest] = useState({ enabled: false, hour: 6 });
  const [runsheet, setRunsheet] = useState({ enabled: true, hour: 20 });
  const [weekly, setWeekly] = useState({ enabled: true, hour: 7 });
  const [noShow, setNoShow] = useState({ enabled: true, mode: "full", flat_fee: 75, percent: 50, bill_wait: true, after_minutes: 15 });
  const [kit, setKit] = useState(null);
  const [vehicleList, setVehicleList] = useState([]);
  const [copied, setCopied] = useState("");

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(""), 2000);
    } catch {
      setError("Your browser blocked the clipboard — select the text and copy it manually.");
    }
  };

  useEffect(() => {
    if (!company) return;
    setProfile({
      name: company.name || "", address: company.address || "", phone: company.phone || "",
      email: company.email || "", logo_url: company.logo_url || "", tax_id: company.tax_id || "",
    });
    setBranding({ ...branding, ...(company.branding || {}) });
    setContact({
      contact_phone: company.contact_phone || "", contact_email: company.contact_email || "",
      contact_hours: company.contact_hours || "", contact_note: company.contact_note || "",
    });
    setDomain(company.custom_domain || "");
    setVanity(company.vanity_domain || "");
    setHandle(company.handle || "");
    setEmailCfg({
      email_mode: company.email_mode || "standard", reply_to_email: company.reply_to_email || "",
      sender_name: company.sender_name || "", sender_email: company.sender_email || "",
    });
    setKeys((k) => ({ ...k, stripe_publishable_key: company.stripe_publishable_key || "" }));
    setRules((r) => ({ ...r, ...(company.billing_rules || {}), dispatch_alert_number: company.dispatch_alert_number || "", dispatch_alert_email: company.dispatch_alert_email || "", passenger_changes_enabled: company.passenger_changes_enabled !== false, change_cutoff_hours: company.change_cutoff_hours ?? 24, change_fee: company.change_fee ?? 0, change_fee_within_hours: company.change_fee_within_hours ?? 4, timezone: company.timezone || "UTC" }));
    setDigest({ enabled: !!company.digest_enabled, hour: company.digest_hour ?? 6 });
    setRunsheet({ enabled: company.runsheet_enabled !== false, hour: company.runsheet_hour ?? 20 });
    setWeekly({ enabled: company.weekly_report_enabled !== false, hour: company.weekly_report_hour ?? 7 });
    setNoShow((n) => ({ ...n, ...(company.no_show_policy || {}) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  useEffect(() => {
    api.get("/sms/status").then(({ data }) => setSms(data)).catch(() => {});
    api.get("/vehicles").then(({ data }) => setVehicleList(data)).catch(() => {});
  }, []);

  const save = async (fn) => {
    setBusy(true);
    setMsg("");
    setError("");
    try {
      const result = await fn();
      // Guard: a caller that forgets `.data` would otherwise poison global company state.
      const payload = result?.data ?? result;
      if (payload) setCompany(payload?.company || payload);
      setMsg("Saved.");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const SaveButton = ({ testId, onClick, label = "Save changes" }) => (
    <Button data-testid={testId} disabled={busy} onClick={onClick} className="h-11 bg-gold px-8 text-ink-900 hover:bg-gold-light">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : label}
    </Button>
  );

  return (
    <div data-testid="settings-page" className="max-w-3xl space-y-8">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.28em] text-gold">Configuration</p>
        <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-50">Settings</h1>
      </div>

      {msg && <p data-testid="settings-success" className="text-sm text-emerald-400">{msg}</p>}
      <MapsStatusBanner />
      {error && <p data-testid="settings-error" className="text-sm text-red-400">{error}</p>}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 bg-ink-800 p-1">
          {[["company", "Company"], ["branding", "Branding"], ["myapp", "My app"], ["operations", "Operations"], ["rates", "Rates & rewards"], ["domain", "Domain"], ["email", "Email"], ["payments", "Payments"]].map(([k, l]) => (
            <TabsTrigger key={k} value={k} data-testid={`settings-tab-${k}`} className="data-[state=active]:bg-gold data-[state=active]:text-ink-900">
              {l}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="company" className="mt-6">
          <Panel title="Company information" description="Printed on every manifest and included in driver emails.">
            <Field label="Company name" testId="settings-company-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} />
            <Field label="Company address" testId="settings-company-address" value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })} />
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Field label="Company phone" testId="settings-company-phone" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} />
              <Field label="Company email" testId="settings-company-email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} />
            </div>
            <Field label="EIN / Tax ID (optional)" testId="settings-company-ein" value={profile.tax_id}
              onChange={(e) => setProfile({ ...profile, tax_id: e.target.value })} placeholder="12-3456789" />
            <p className="text-xs text-slate-500">
              Your EIN prints as the payer tax ID on every chauffeur 1099-NEC worksheet under Payouts & 1099.
            </p>
            <Field label="Logo URL" testId="settings-company-logo" value={profile.logo_url} onChange={(e) => setProfile({ ...profile, logo_url: e.target.value })} placeholder="https://yourdomain.com/logo.png" />
            <div>
              <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-gold">Or upload your logo</label>
              <input
                data-testid="settings-logo-upload"
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => save(async () => (await api.post("/company/logo", { data_url: reader.result })).data);
                  reader.readAsDataURL(file);
                }}
                className="block w-full cursor-pointer rounded-md border border-ink-600 bg-ink-800 p-2.5 text-sm text-slate-300 file:mr-3 file:rounded file:border-0 file:bg-gold file:px-3 file:py-1.5 file:text-ink-900"
              />
              <p className="mt-1 text-xs text-slate-500">PNG, JPG, SVG or WEBP up to 1 MB. Appears on manifests, invoices, emails and every portal.</p>
            </div>
            {profile.logo_url && (
              <img src={profile.logo_url} alt="Company logo preview" className="h-14 w-auto rounded border border-ink-600 bg-white p-1 object-contain" />
            )}
            <div className="rounded-lg border border-ink-600 bg-ink-900/60 p-4">
              <StoreIconUpload />
            </div>
            <SaveButton testId="settings-company-save" onClick={() => save(async () => (await api.put("/company", profile)).data)} />
            <div data-testid="booking-link-card" className="rounded-lg border border-ink-600 bg-ink-900/60 p-4">
              <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Rider app link</p>
              <p className="mt-2 break-all text-sm text-slate-300">
                {window.location.origin}/ride/{company?.handle || company?.id}
              </p>
              <a
                href={`/ride/${company?.handle || company?.id}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="open-rider-app"
                className="mt-3 inline-block font-mono text-xs text-gold"
              >
                Open rider app →
              </a>
              <p className="mt-2 text-xs text-slate-500">
                Share this with passengers: instant quotes, add-a-stop, deposit payment, live tracking and their own
                trip list. It installs to the home screen like an app. Requests land in Bookings.
              </p>
              <p className="mt-3 break-all font-mono text-[11px] text-slate-500">
                Classic form: {window.location.origin}/book/{company?.id}
              </p>
            </div>
          </Panel>

          <div className="mt-6">
            <Panel title="How passengers reach you"
              description="Shown to passengers on your booking page, in the rider app, in the client portal and at the foot of your blog — a number they can tap.">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field label="Phone for passengers" testId="settings-contact-phone" value={contact.contact_phone}
                  onChange={(e) => setContact({ ...contact, contact_phone: e.target.value })}
                  placeholder={profile.phone || "+1 713 555 0100"} />
                <Field label="Email for passengers" testId="settings-contact-email" value={contact.contact_email}
                  onChange={(e) => setContact({ ...contact, contact_email: e.target.value })}
                  placeholder={profile.email || "reservations@yourcompany.com"} />
              </div>
              <Field label="When you answer" testId="settings-contact-hours" value={contact.contact_hours}
                onChange={(e) => setContact({ ...contact, contact_hours: e.target.value })}
                placeholder="Dispatch answers 24/7" />
              <div>
                <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-gold">
                  One line of guidance (optional)
                </label>
                <Textarea data-testid="settings-contact-note" rows={2} value={contact.contact_note}
                  onChange={(e) => setContact({ ...contact, contact_note: e.target.value })}
                  placeholder="For anything inside two hours of pickup, please call rather than email."
                  className="border-ink-600 bg-ink-800 text-sm text-slate-100 focus-visible:ring-gold" />
              </div>
              <p className="text-xs text-slate-500">
                Leave the phone or email blank and your company phone and email above are used instead.
              </p>
              <SaveButton testId="settings-contact-save"
                onClick={() => save(async () => (await api.put("/company", contact)).data)} />
            </Panel>
          </div>
        </TabsContent>

        <TabsContent value="branding" className="mt-6">
          {hasFlag("branding") ? (
            <Panel title="Custom branding" description="Colors, fonts and document copy across manifests, invoices and portals.">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                <Field label="Brand color" testId="branding-primary-color" type="color" value={branding.primary_color} onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })} />
                <Field label="Header color" testId="branding-accent-color" type="color" value={branding.accent_color} onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })} />
                <Field label="Font" testId="branding-font" value={branding.font} onChange={(e) => setBranding({ ...branding, font: e.target.value })} />
              </div>
              <Field label="Email header text" testId="branding-email-header" value={branding.email_header} onChange={(e) => setBranding({ ...branding, email_header: e.target.value })} />
              <Field label="Email footer text" testId="branding-email-footer" value={branding.email_footer} onChange={(e) => setBranding({ ...branding, email_footer: e.target.value })} />
              <Field label="Manifest footer note">
                <Textarea data-testid="branding-manifest-note" rows={3} value={branding.manifest_note}
                  onChange={(e) => setBranding({ ...branding, manifest_note: e.target.value })}
                  className="border-ink-600 bg-ink-800 text-slate-100 focus-visible:ring-gold" />
              </Field>
              <SaveButton testId="branding-save" onClick={() => save(async () => (await api.put("/company/branding", branding)).data)} />
            </Panel>
          ) : (
            <LockedFeature
              testId="branding-locked"
              title="Custom branding is a paid upgrade"
              description="Free and Starter operators get company name, logo and contact details on manifests. Brand colors, fonts, custom manifest and invoice design, and portal branding unlock on Business / Branded."
            />
          )}
        </TabsContent>

        <TabsContent value="myapp" className="mt-6">
          <Panel title="My app" description="Your own passenger app on Google Play and the App Store — your name, your icon, your colours. Passengers install it and book from their home screen.">
            <AppDesigner />
          </Panel>
        </TabsContent>

        <TabsContent value="operations" className="mt-6">
          <Panel title="Trip operations" description="Control how mid-trip stop requests from passengers are handled.">
            <div className="space-y-3">
              {[
                { key: "automatic", title: "Automatic", body: "Passenger adds a stop and it is added to the itinerary immediately." },
                { key: "driver", title: "Driver approval", body: "The chauffeur sees the request and accepts or declines it." },
                { key: "dispatcher", title: "Dispatcher approval", body: "Dispatch reviews the request before it reaches the chauffeur." },
              ].map((o) => (
                <button
                  type="button"
                  key={o.key}
                  data-testid={`stop-mode-${o.key}`}
                  onClick={() => save(async () => (await api.put("/company/operations", { stop_approval_mode: o.key })).data)}
                  className={`w-full rounded-lg border p-4 text-left transition-colors ${
                    company?.stop_approval_mode === o.key ? "border-gold/50 bg-gold/10" : "border-ink-600 bg-ink-800 hover:border-gold/40"
                  }`}
                >
                  <p className="text-sm font-medium text-slate-100">{o.title}</p>
                  <p className="mt-1 text-xs text-slate-400">{o.body}</p>
                </button>
              ))}
            </div>
            <div className="rounded-lg border border-ink-600 bg-ink-900/60 p-4">
              <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Driver financial visibility</p>
              <p className="mt-2 text-sm text-slate-300">Hidden — chauffeurs never see customer totals, rates, deposits, balances or payment methods.</p>
            </div>
          </Panel>

          <div className="mt-6">
            <Panel title="Wait time & overtime" description="Metered automatically from the chauffeur's own lifecycle timestamps at trip close-out. Nothing is billed inside the free grace window.">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                <Field label="Standard grace (min)" testId="rules-grace" type="number" min="0" value={rules.grace_minutes}
                  onChange={(e) => setRules({ ...rules, grace_minutes: Number(e.target.value) })} />
                <Field label="Airport grace (min)" testId="rules-airport-grace" type="number" min="0" value={rules.airport_grace_minutes}
                  onChange={(e) => setRules({ ...rules, airport_grace_minutes: Number(e.target.value) })} />
                <Field label="Extra-stop grace (min)" testId="rules-stop-grace" type="number" min="0" value={rules.stop_grace_minutes}
                  onChange={(e) => setRules({ ...rules, stop_grace_minutes: Number(e.target.value) })} />
              </div>
              <Field label="Dispatch alert number (optional)" testId="rules-alert-number" value={rules.dispatch_alert_number}
                onChange={(e) => setRules({ ...rules, dispatch_alert_number: e.target.value })}
                placeholder={company?.phone || "+1 713 555 0100"} />
              <Field label="Dispatch alert email" testId="rules-alert-email" type="email" value={rules.dispatch_alert_email}
                onChange={(e) => setRules({ ...rules, dispatch_alert_email: e.target.value })}
                placeholder={company?.email || "dispatch@yourcompany.com"} />
              <p className="text-xs text-slate-500">
                Wait-time and late-pickup alerts are emailed here. Leave it blank to use your company
                email. Texts go out as well once Twilio is connected.
              </p>

              <div className="rounded-lg border border-ink-600 bg-ink-900/60 p-4">
                <p className="font-mono text-[10px] uppercase tracking-widest text-gold">Passenger changes</p>
                <p className="mt-2 text-xs text-slate-400">
                  How long before pickup a passenger can still change or cancel their own reservation
                  from their trips page. After that they are asked to call dispatch.
                </p>
                <button type="button" data-testid="passenger-changes-toggle"
                  onClick={() => setRules({ ...rules, passenger_changes_enabled: !rules.passenger_changes_enabled })}
                  className={`mt-3 w-full rounded-lg border p-3 text-left text-sm transition-colors ${
                    rules.passenger_changes_enabled
                      ? "border-gold/50 bg-gold/10 text-slate-100"
                      : "border-ink-600 bg-ink-800 text-slate-400 hover:border-gold/40"}`}>
                  {rules.passenger_changes_enabled
                    ? "Passengers can change and cancel their own rides"
                    : "All changes go through dispatch by phone"}
                </button>
                {rules.passenger_changes_enabled && (
                  <>
                    <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                      {[2, 4, 12, 24, 48].map((h) => (
                        <button key={h} type="button" data-testid={`cutoff-${h}`}
                          onClick={() => setRules({ ...rules, change_cutoff_hours: h })}
                          className={`rounded-lg border py-2 font-mono text-xs transition-colors ${
                            Number(rules.change_cutoff_hours) === h
                              ? "border-gold/50 bg-gold/10 text-gold"
                              : "border-ink-600 bg-ink-800 text-slate-400 hover:border-gold/40"}`}>
                          {h}h before
                        </button>
                      ))}
                    </div>
                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Field label="Change fee ($)" testId="rules-change-fee" type="number" min="0" step="5"
                        value={rules.change_fee}
                        onChange={(e) => setRules({ ...rules, change_fee: e.target.value })} />
                      <Field label="Fee applies within (hours)" testId="rules-change-fee-window" type="number" min="0" step="1"
                        value={rules.change_fee_within_hours}
                        onChange={(e) => setRules({ ...rules, change_fee_within_hours: e.target.value })} />
                    </div>
                    <p className="mt-2 text-xs text-slate-500">
                      Set the fee to 0 for free changes. The passenger sees the fee before they
                      confirm, and it is added to the trip total.
                    </p>
                    {Number(rules.change_fee) > 0
                      && Number(rules.change_cutoff_hours) >= Number(rules.change_fee_within_hours) && (
                      <p data-testid="fee-unreachable-warning" className="mt-2 rounded border border-amber-400/40 bg-amber-400/10 p-2.5 text-xs text-amber-200">
                        Heads up: changes already close {rules.change_cutoff_hours}h before pickup, so a
                        fee inside {rules.change_fee_within_hours}h will never apply. Lower the cutoff
                        below the fee window to charge for late reschedules.
                      </p>
                    )}
                  </>
                )}
              </div>
              <button
                type="button"
                data-testid="rules-auto-apply"
                onClick={() => setRules({ ...rules, auto_apply: !rules.auto_apply })}
                className={`w-full rounded-lg border p-4 text-left transition-colors ${
                  rules.auto_apply ? "border-gold/50 bg-gold/10" : "border-ink-600 bg-ink-800 hover:border-gold/40"}`}
              >
                <p className="text-sm font-medium text-slate-100">
                  {rules.auto_apply ? "Applied automatically at close-out" : "Metered but held for review"}
                </p>
                <p className="mt-1 text-xs text-slate-400">
                  {rules.auto_apply
                    ? "Billable wait and overtime land on the final calculation the moment the trip completes. You can still override the numbers before invoicing."
                    : "Wait and overtime are measured and shown on the manifest, but never added to the fare until you recalculate."}
                </p>
              </button>
              <p className="text-xs text-slate-500">
                Wait time bills at each vehicle's waiting rate and overtime at its overtime rate — set those under Vehicles.
                The moment a wait passes its grace window, dispatch gets an email (and a text once Twilio is connected)
                and the trip shows a live billing badge on the dispatch board.
              </p>
              <SaveButton testId="rules-save" onClick={() => save(async () => (await api.put("/company/billing-rules", rules)).data)} />
            </Panel>
          </div>

          <div className="mt-6">
            <Panel title="Late pickup alerts" description="Dispatch gets a heads-up before a pickup that nobody has started toward, then a firmer alert the moment the pickup time passes.">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field label="Heads-up lead time (min)" testId="rules-late-lead" type="number" min="0" value={rules.late_pickup_lead_minutes}
                  onChange={(e) => setRules({ ...rules, late_pickup_lead_minutes: Number(e.target.value) })} />
                <Field label="Time zone (IANA)" testId="rules-timezone" value={rules.timezone}
                  onChange={(e) => setRules({ ...rules, timezone: e.target.value })}
                  placeholder="America/Chicago" />
              </div>
              <button
                type="button"
                data-testid="rules-late-toggle"
                onClick={() => setRules({ ...rules, late_pickup_alerts: !rules.late_pickup_alerts })}
                className={`w-full rounded-lg border p-4 text-left transition-colors ${
                  rules.late_pickup_alerts ? "border-gold/50 bg-gold/10" : "border-ink-600 bg-ink-800 hover:border-gold/40"}`}
              >
                <p className="text-sm font-medium text-slate-100">
                  {rules.late_pickup_alerts ? "Late pickup alerts are on" : "Late pickup alerts are off"}
                </p>
                <p className="mt-1 text-xs text-slate-400">
                  {rules.late_pickup_alerts
                    ? "A trip still sitting at booked, assigned or accepted inside the lead time raises one heads-up, and one more once the pickup time passes."
                    : "Nobody is warned about pickups that haven't been started."}
                </p>
              </button>
              <p className="text-xs text-slate-500">
                Your time zone decides when a pickup counts as late — pickup times are read on that clock.
                Alerts land in the bell in the header and text the dispatch number above.
              </p>
              <SaveButton testId="rules-late-save" onClick={() => save(async () => (await api.put("/company/billing-rules", rules)).data)} />
            </Panel>
          </div>

          <div className="mt-6">
            <Panel title="Passenger heads-up" description="Your passenger gets one automatic message the moment their chauffeur is minutes away — email, text if they opted in, and a notification in the installed app.">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field label="Alert when the car is this close (min)" testId="rules-proximity" type="number" min="0" max="60"
                  value={rules.proximity_alert_minutes}
                  onChange={(e) => setRules({ ...rules, proximity_alert_minutes: Number(e.target.value) })} />
              </div>
              <p className="text-xs text-slate-500">
                Set 0 to switch it off. The heads-up fires once per trip, on the live traffic-aware
                drive time from the chauffeur's GPS to the pickup.
              </p>
              <SaveButton testId="rules-proximity-save" onClick={() => save(async () => (await api.put("/company/billing-rules", rules)).data)} />
            </Panel>
          </div>

          <div className="mt-6">
            <Panel title="Daily ops digest" description="One email each morning to you and your dispatchers: today's runs, anything still without a chauffeur, and yesterday's takings.">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-gold">Send at (your local time)</label>
                  <select data-testid="digest-hour" value={digest.hour}
                    onChange={(e) => setDigest({ ...digest, hour: Number(e.target.value) })}
                    className="h-11 w-full rounded-lg border border-ink-600 bg-ink-800 px-3 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-gold">
                    {Array.from({ length: 24 }, (_, h) => (
                      <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  data-testid="digest-toggle"
                  onClick={() => setDigest({ ...digest, enabled: !digest.enabled })}
                  className={`mt-7 h-11 rounded-lg border px-4 font-mono text-[11px] uppercase tracking-widest transition-colors ${
                    digest.enabled ? "border-gold/50 bg-gold/10 text-gold" : "border-ink-600 text-slate-400 hover:text-slate-100"}`}
                >
                  {digest.enabled ? "Digest is on" : "Digest is off"}
                </button>
              </div>
              <p className="text-xs text-slate-500">
                Goes to every owner, manager and dispatcher on the account. Uses the time zone above.
              </p>
              <div className="flex flex-wrap gap-3">
                <SaveButton testId="digest-save" onClick={() => save(async () => (await api.put("/company/digest", digest)).data)} />
                <Button data-testid="digest-send-now" variant="outline" disabled={busy}
                  onClick={async () => {
                    setBusy(true); setError(""); setMsg("");
                    try {
                      const { data } = await api.post("/reports/digest/send");
                      setMsg(`Digest sent to ${data.recipients.length} recipient(s)` +
                        (data.failed?.length ? ` · ${data.failed.length} could not be delivered.` : "."));
                    } catch (e) {
                      setError(errMsg(e));
                    } finally {
                      setBusy(false);
                    }
                  }}
                  className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">
                  Send me one now
                </Button>
              </div>
            </Panel>
          </div>

          <div className="mt-6">
            <Panel title="No-show policy" description="Your rules for a passenger who never boards. The chauffeur can only bill it after they have arrived and waited out your window — dispatch can reverse it if it is disputed.">
                <button type="button" data-testid="no-show-toggle"
                  onClick={() => setNoShow({ ...noShow, enabled: !noShow.enabled })}
                  className={`w-full rounded-lg border p-3 text-left text-sm transition-colors ${
                    noShow.enabled ? "border-gold/50 bg-gold/10 text-slate-100"
                      : "border-ink-600 bg-ink-800 text-slate-400 hover:border-gold/40"}`}>
                  {noShow.enabled ? "Chauffeurs can bill a no-show" : "No-show billing is off"}
                </button>
                {noShow.enabled && (
                  <>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                      {[["full", "Full trip price"], ["percent", "% of the trip"], ["flat", "Flat fee"]].map(([mode, label]) => (
                        <button key={mode} type="button" data-testid={`no-show-mode-${mode}`}
                          onClick={() => setNoShow({ ...noShow, mode })}
                          className={`rounded-lg border py-2.5 text-xs transition-colors ${
                            noShow.mode === mode ? "border-gold/50 bg-gold/10 text-gold"
                              : "border-ink-600 bg-ink-800 text-slate-400 hover:border-gold/40"}`}>
                          {label}
                        </button>
                      ))}
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {noShow.mode === "flat" && (
                        <Field label="Flat no-show fee ($)" testId="no-show-flat" type="number" min="0" step="5"
                          value={noShow.flat_fee}
                          onChange={(e) => setNoShow({ ...noShow, flat_fee: e.target.value })} />
                      )}
                      {noShow.mode === "percent" && (
                        <Field label="Percent of the trip (%)" testId="no-show-percent" type="number" min="0" max="100" step="5"
                          value={noShow.percent}
                          onChange={(e) => setNoShow({ ...noShow, percent: e.target.value })} />
                      )}
                      <Field label="Billable after extra wait (min)" testId="no-show-minutes" type="number" min="0" step="5"
                        value={noShow.after_minutes}
                        onChange={(e) => setNoShow({ ...noShow, after_minutes: e.target.value })} />
                    </div>
                    <button type="button" data-testid="no-show-bill-wait"
                      onClick={() => setNoShow({ ...noShow, bill_wait: !noShow.bill_wait })}
                      className={`w-full rounded-lg border p-3 text-left text-xs transition-colors ${
                        noShow.bill_wait ? "border-gold/50 bg-gold/10 text-slate-100"
                          : "border-ink-600 bg-ink-800 text-slate-400 hover:border-gold/40"}`}>
                      {noShow.bill_wait
                        ? "Also bill the waiting time the chauffeur burned"
                        : "Charge the no-show fee only"}
                    </button>
                    <p className="text-xs text-slate-500">
                      The extra wait is on top of your grace window, so a 45 min airport grace plus
                      {" "}{noShow.after_minutes} min means the button unlocks at{" "}
                      {45 + Number(noShow.after_minutes || 0)} min on scene.
                    </p>
                  </>
                )}
                <SaveButton testId="no-show-save"
                  onClick={() => save(async () => (await api.put("/company/no-show-policy", {
                    ...noShow,
                    flat_fee: Number(noShow.flat_fee || 0),
                    percent: Number(noShow.percent || 0),
                    after_minutes: Number(noShow.after_minutes || 0),
                  })).data)} />
            </Panel>
          </div>

          <div className="mt-6">
            <Panel title="Evening run sheets" description="Each chauffeur gets tomorrow's runs by email every evening — their own trips only, with times, passengers, addresses, flights and notes.">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-gold">Send at (your local time)</label>
                  <select data-testid="runsheet-hour" value={runsheet.hour}
                    onChange={(e) => setRunsheet({ ...runsheet, hour: Number(e.target.value) })}
                    className="h-11 w-full rounded-lg border border-ink-600 bg-ink-800 px-3 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-gold">
                    {Array.from({ length: 24 }, (_, h) => (
                      <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
                    ))}
                  </select>
                </div>
                <button type="button" data-testid="runsheet-toggle"
                  onClick={() => setRunsheet({ ...runsheet, enabled: !runsheet.enabled })}
                  className={`mt-7 h-11 rounded-lg border px-4 font-mono text-[11px] uppercase tracking-widest transition-colors ${
                    runsheet.enabled ? "border-gold/50 bg-gold/10 text-gold" : "border-ink-600 text-slate-400 hover:text-slate-100"}`}>
                  {runsheet.enabled ? "Run sheets are on" : "Run sheets are off"}
                </button>
              </div>
              <p className="text-xs text-slate-500">
                A chauffeur with nothing tomorrow gets no email. Unassigned trips are never sent out.
              </p>
              <div className="flex flex-wrap gap-3">
                <SaveButton testId="runsheet-save" onClick={() => save(async () => (await api.put("/company/runsheet", runsheet)).data)} />
                <Button data-testid="runsheet-send-now" variant="outline" disabled={busy}
                  onClick={async () => {
                    setBusy(true); setError(""); setMsg("");
                    try {
                      const { data } = await api.post("/reports/runsheet/send");
                      setMsg(`Run sheets for ${data.date} sent to ${data.recipients.length} chauffeur(s).`);
                    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
                  }}
                  className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">
                  Send tomorrow's now
                </Button>
              </div>
            </Panel>
          </div>

          <div className="mt-6">
            <Panel title="Weekly owner report" description="Monday morning recap for owners and managers: last week's trips, revenue, tips, chauffeur payouts, what is still owed and your best customers.">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-gold">Monday send time</label>
                  <select data-testid="weekly-hour" value={weekly.hour}
                    onChange={(e) => setWeekly({ ...weekly, hour: Number(e.target.value) })}
                    className="h-11 w-full rounded-lg border border-ink-600 bg-ink-800 px-3 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-gold">
                    {Array.from({ length: 24 }, (_, h) => (
                      <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
                    ))}
                  </select>
                </div>
                <button type="button" data-testid="weekly-toggle"
                  onClick={() => setWeekly({ ...weekly, enabled: !weekly.enabled })}
                  className={`mt-7 h-11 rounded-lg border px-4 font-mono text-[11px] uppercase tracking-widest transition-colors ${
                    weekly.enabled ? "border-gold/50 bg-gold/10 text-gold" : "border-ink-600 text-slate-400 hover:text-slate-100"}`}>
                  {weekly.enabled ? "Weekly report is on" : "Weekly report is off"}
                </button>
              </div>
              <div className="flex flex-wrap gap-3">
                <SaveButton testId="weekly-save" onClick={() => save(async () => (await api.put("/company/weekly-report", weekly)).data)} />
                <Button data-testid="weekly-send-now" variant="outline" disabled={busy}
                  onClick={async () => {
                    setBusy(true); setError(""); setMsg("");
                    try {
                      const { data } = await api.post("/reports/weekly/send");
                      setMsg(`Last week's report sent to ${data.recipients.length} recipient(s).`);
                    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
                  }}
                  className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">
                  Send last week now
                </Button>
              </div>
            </Panel>
          </div>
        </TabsContent>

        <TabsContent value="rates" className="mt-6 space-y-8">
          <Panel title="Flat airport rates" description="Fixed prices for your busiest airport runs — checked before mileage.">
            <FlatRates vehicles={vehicleList} handle={company?.handle || company?.id} />
          </Panel>
          <Panel title="Loyalty rewards" description="Earn repeat passengers a credit off their next ride.">
            <LoyaltyPanel company={company} setCompany={setCompany} />
          </Panel>
        </TabsContent>


        <TabsContent value="domain" className="mt-6 space-y-6">
          <Panel title="Your branded link" description="A booking page on the platform domain that carries your name, logo and colours. No DNS, live the moment you save it.">
            <div className="rounded-lg border border-gold/25 bg-gold/5 p-4">
              <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Live now</p>
              <p data-testid="branded-link" className="mt-2 break-all text-sm text-slate-200">
                {window.location.origin}/ride/{company?.handle || company?.id}
              </p>
              <button data-testid="branded-link-copy"
                onClick={() => copy(
                  `${window.location.origin}/ride/${company?.handle || company?.id}`, "branded")}
                className="mt-3 font-mono text-[11px] uppercase tracking-widest text-slate-400 hover:text-gold">
                {copied === "branded" ? "Copied" : "Copy link"}
              </button>
            </div>
            <Field label="Your handle" testId="handle-input" value={handle}
              onChange={(e) => setHandle(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
              placeholder="acmelimo" />
            <p className="text-xs text-slate-500">
              Lowercase letters, numbers and hyphens. Share it as{" "}
              <span className="text-slate-300">{window.location.host}/ride/{handle || "yourname"}</span>.
            </p>
            <SaveButton testId="handle-save"
              onClick={() => save(async () => (await api.put("/company/handle", { handle })).data)} />
          </Panel>

          <Panel title="Link kit" description="Everything you need to publish your booking link — on your website, your Google listing, your socials and your email signature.">
            {!kit ? (
              <Button data-testid="link-kit-load" disabled={busy}
                onClick={() => save(async () => {
                  const { data } = await api.get("/company/link-kit");
                  setKit(data);
                  return null;
                })}
                className="bg-gold text-ink-900 hover:bg-gold-light">
                Build my link kit
              </Button>
            ) : (
              <div data-testid="link-kit" className="space-y-5">
                <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
                  <img data-testid="link-kit-qr" src={kit.qr_data_url} alt="Booking link QR code"
                    className="h-40 w-40 shrink-0 rounded-lg bg-white p-2" />
                  <div className="min-w-0">
                    <p className="break-all font-mono text-xs text-slate-300">{kit.link}</p>
                    <p className="mt-2 text-xs text-slate-500">
                      Print the QR on cards, invoices and the back of the headrest — a phone camera
                      opens your booking page.
                    </p>
                    <a data-testid="link-kit-download" href={kit.qr_data_url} download="booking-qr.png"
                      className="mt-3 inline-block font-mono text-[11px] uppercase tracking-widest text-gold">
                      Download the QR
                    </a>
                  </div>
                </div>
                {[["website", "For your website"], ["google", "For your Google listing"],
                  ["social", "For social media"], ["sms", "For a text message"],
                  ["signature", "For your email signature"]].map(([key, label]) => (
                  <div key={key} className="rounded-lg border border-ink-600 bg-ink-900/60 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
                      <button type="button" data-testid={`link-kit-copy-${key}`}
                        onClick={() => copy(kit.blurbs[key], key)}
                        className="font-mono text-[10px] uppercase tracking-widest text-slate-400 hover:text-gold">
                        {copied === key ? "Copied" : "Copy"}
                      </button>
                    </div>
                    <p data-testid={`link-kit-text-${key}`} className="mt-2 text-sm leading-relaxed text-slate-300">
                      {kit.blurbs[key]}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          <Panel title="Company finder" description="Passengers who find you on Google but not your link can search for you on the LimoLinkX passenger app and land on your booking page.">
            <button type="button" data-testid="directory-toggle"
              onClick={() => save(async () => (await api.put("/company/directory-listing",
                { directory_listed: !(company?.directory_listed ?? true) })).data)}
              className={`w-full rounded-lg border p-4 text-left transition-colors ${
                (company?.directory_listed ?? true)
                  ? "border-gold/50 bg-gold/10" : "border-ink-600 bg-ink-800 hover:border-gold/40"}`}>
              <p className="text-sm font-medium text-slate-100">
                {(company?.directory_listed ?? true)
                  ? "Listed in the passenger company finder"
                  : "Hidden from the company finder"}
              </p>
              <p className="mt-1 text-xs text-slate-400">
                {(company?.directory_listed ?? true)
                  ? "Your name, city and logo appear when a passenger searches for you. Only your public booking page is shared."
                  : "Only passengers with your direct link can reach your booking page."}
              </p>
            </button>
          </Panel>

          {hasFlag("custom_domain") ? (
            <Panel title="Bring your own domain" description="Prefer book.yourcompany.com? Tell us the hostname and we set up the DNS and SSL for you, then confirm when it is live.">
              <Field label="Your hostname" testId="domain-input" value={domain}
                onChange={(e) => setDomain(e.target.value.trim().toLowerCase())}
                placeholder="book.acmelimo.com" />
              <SaveButton testId="domain-save" label="Request this domain"
                onClick={() => save(async () => {
                  const { data } = await api.put("/company/domain", { custom_domain: domain });
                  setDns(data.dns);
                  return data.company;
                })} />
              {company?.custom_domain && (
                <div data-testid="domain-status" className="rounded-lg border border-ink-600 bg-ink-900/60 p-5">
                  <p className="font-mono text-[11px] uppercase tracking-widest text-gold">
                    {company.custom_domain} · {DOMAIN_STATES[company.domain_status]?.label || company.domain_status}
                  </p>
                  <p className="mt-2 text-xs text-slate-400">
                    {DOMAIN_STATES[company.domain_status]?.hint
                      || "We will email you when there is an update."}
                  </p>
                  {company.domain_note && (
                    <p className="mt-2 text-xs text-slate-300">Note from support: {company.domain_note}</p>
                  )}
                  {(dns.length > 0 || company.domain_status === "in_progress") && (
                    <div className="mt-4 space-y-3 font-mono text-xs text-slate-300">
                      {dns.map((r) => (
                        <div key={`${r.type}-${r.host}`} data-testid={`dns-record-${r.type}`}
                          className="grid grid-cols-1 gap-1 border-b border-ink-600 pb-2 sm:grid-cols-4">
                          <span className="text-gold">{r.type}</span>
                          <span className="break-all">{r.host}</span>
                          <span className="break-all">{r.value}</span>
                          <span className="text-slate-500">TTL {r.ttl}</span>
                        </div>
                      ))}
                      <p className="pt-1 font-sans text-xs text-slate-500">
                        Add this record at your DNS provider — support finishes the SSL step from our side.
                      </p>
                    </div>
                  )}
                  <p className="mt-4 text-xs text-slate-500">
                    Your branded link above keeps working the whole time.
                  </p>
                </div>
              )}
            </Panel>
          ) : (
            <LockedFeature
              testId="domain-locked"
              title="Your own domain is a paid upgrade"
              description="Run booking on book.yourcompany.com with SSL, set up for you by our team. Available from Business / Branded. Your branded link on the platform domain works on every plan."
            />
          )}
        </TabsContent>

        <TabsContent value="email" className="mt-6">
          <Panel title="Customer-facing email" description="Choose how outbound email is presented to customers and drivers.">
            <div className="space-y-3">
              {[
                { key: "standard", title: "Standard", body: `Emails sent from the platform: "${company?.name || "Your Company"} via ${PLATFORM_NAME}".`, locked: false },
                { key: "professional", title: "Professional", body: "Emails appear fully under your branding with your reply-to address.", locked: !hasFlag("custom_email") },
                { key: "enterprise", title: "Enterprise", body: "Full custom sender domain and template configuration.", locked: !hasFlag("custom_email") },
              ].map((o) => (
                <button
                  type="button"
                  key={o.key}
                  data-testid={`email-mode-${o.key}`}
                  disabled={o.locked}
                  onClick={() => setEmailCfg({ ...emailCfg, email_mode: o.key })}
                  className={`w-full rounded-lg border p-4 text-left transition-colors ${
                    emailCfg.email_mode === o.key ? "border-gold/50 bg-gold/10" : "border-ink-600 bg-ink-800"
                  } ${o.locked ? "cursor-not-allowed opacity-55" : "hover:border-gold/40"}`}
                >
                  <p className="flex items-center gap-2 text-sm font-medium text-slate-100">
                    {o.title}
                    {o.locked && (
                      <span className="rounded-full border border-gold/30 bg-gold/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-gold">
                        Paid upgrade
                      </span>
                    )}
                  </p>
                  <p className="mt-1 text-xs text-slate-400">{o.body}</p>
                </button>
              ))}
            </div>
            <Field label="Reply-to address" testId="email-reply-to" value={emailCfg.reply_to_email} onChange={(e) => setEmailCfg({ ...emailCfg, reply_to_email: e.target.value })} placeholder="reservations@abctransportation.com" />
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Field label="Sender name" testId="email-sender-name" value={emailCfg.sender_name} onChange={(e) => setEmailCfg({ ...emailCfg, sender_name: e.target.value })} placeholder="ABC Executive Transportation" />
              <Field label="Sender address" testId="email-sender-email" value={emailCfg.sender_email} onChange={(e) => setEmailCfg({ ...emailCfg, sender_email: e.target.value })} placeholder="trips@abctransportation.com" />
            </div>
            <p className="text-xs text-slate-500">
              Sender name and address appear on every manifest, invoice, tracking and delay email. To send from your
              own domain, verify that domain in your email provider first — until then replies route to the address
              above.
            </p>
            <SaveButton testId="email-save" onClick={() => save(async () => (await api.put("/company/email-settings", emailCfg)).data)} />
            <div data-testid="sms-card" className="rounded-lg border border-ink-600 bg-ink-900/60 p-5">
              <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Text messages</p>
              {!sms.plan_allows ? (
                <p data-testid="sms-plan-locked" className="mt-2 text-sm text-amber-300">
                  Text message trip updates are part of {sms.plan_required || "Professional"} and above.
                  On your plan every update goes out by email instead — upgrade and passengers who tick the
                  consent box start getting their tracking link by text.
                </p>
              ) : sms.configured ? (
                <p data-testid="sms-live" className="mt-2 text-sm text-emerald-400">
                  Texting is live from <span className="font-mono">{sms.from_number}</span> — new bookings text your
                  dispatch number and passengers get their tracking link by text.
                </p>
              ) : sms.credentials_ready ? (
                <p data-testid="sms-needs-number" className="mt-2 text-sm text-amber-300">
                  Your Twilio account is connected. The last step is a Twilio phone number to send from — buy one in
                  Twilio under Phone Numbers → Buy a number, then send it over and texting goes live.
                </p>
              ) : (
                <p className="mt-2 text-sm text-slate-300">
                  Trip updates go out by <span className="text-slate-100">email</span>. Texting stays off until a
                  Twilio account and number are connected.
                </p>
              )}
              <p className="mt-2 text-xs text-slate-500">
                When on, passengers are emailed their live tracking link when the chauffeur is assigned, on the way,
                has arrived, has started the trip and has finished it.
              </p>
              <Button
                data-testid="sms-toggle"
                variant="outline"
                disabled={busy}
                onClick={() => save(async () => (await api.put("/company/sms", { sms_enabled: !company?.sms_enabled })).data)}
                className="mt-4 border-ink-600 bg-transparent text-slate-300 hover:text-gold"
              >
                {company?.sms_enabled ? "Turn trip updates off" : "Turn trip updates on"}
              </Button>
              <div data-testid="sms-a2p" className="mt-5 rounded-lg border border-ink-600 bg-ink-800/60 p-4">
                <p className="font-mono text-[11px] uppercase tracking-widest text-slate-400">
                  Carrier registration · A2P 10DLC
                </p>
                <p className="mt-2 text-xs leading-relaxed text-slate-500">
                  US carriers vet every texting programme and ask for a public page proving how your
                  passengers consent. This link shows your business name, the exact consent checkbox a
                  passenger ticks and your sample messages, with no login — paste it into the opt-in and
                  message-flow fields of your Twilio campaign.
                </p>
                <CopyLink testId="sms-optin" url={`${window.location.origin}/legal/sms-optin/${company?.handle || company?.id || ""}`} />
              </div>
            </div>
          </Panel>
        </TabsContent>

        <TabsContent value="payments" className="mt-6">
          {hasFlag("payments_byok") ? (
            <div className="space-y-6">
              <PaymentPolicyPanel onCompany={setCompany} />
              <PayoutPolicyCard />
              <Panel title="Stripe" description="Your own Stripe keys — passenger deposits and balances settle straight into your Stripe balance. The platform never holds passenger money.">
                {company?.payments_sandbox && (
                  <div data-testid="stripe-test-warning" className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
                    <p className="font-mono text-[11px] uppercase tracking-widest text-amber-300">Test mode</p>
                    <p className="mt-2 text-sm text-slate-300">
                      These are test keys (sk_test…), so no real money moves — use card 4242 4242 4242 4242
                      to try a payment. Paste your live keys when you're ready to take real bookings.
                    </p>
                  </div>
                )}
                <Field label="Stripe publishable key" testId="stripe-publishable" value={keys.stripe_publishable_key} onChange={(e) => setKeys({ ...keys, stripe_publishable_key: e.target.value })} placeholder="pk_live_…" />
                <Field label="Stripe secret key" testId="stripe-secret" type="password" value={keys.stripe_secret_key} onChange={(e) => setKeys({ ...keys, stripe_secret_key: e.target.value })} placeholder={company?.stripe_secret_key_set ? "•••••••• saved" : "sk_live_…"} />
                <SaveButton testId="payments-save" onClick={() => save(async () => (await api.put("/company/payment-keys", keys)).data)} />
                {company?.online_payments_ready && (
                  <div data-testid="stripe-connected" className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4">
                    <p className="font-mono text-[11px] uppercase tracking-widest text-emerald-300">
                      Connected · card payments are live on your booking pages
                    </p>
                    <p className="mt-2 text-xs text-slate-400">
                      Find your keys in the Stripe dashboard under Developers → API keys.
                    </p>
                  </div>
                )}
              </Panel>
            </div>
          ) : (
            <LockedFeature
              testId="payments-locked"
              requiredPlan="Starter"
              title="Card payments are a paid feature"
              description="Connect your own Stripe account so deposits and balances settle straight to you. Available from the Starter plan."
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
